'use client'

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { BackendAuth } from '@/lib/auth-backend'
import { isTrialSession } from '@/lib/trial-session'

const DEFAULTS = {
  enabled: false,
  settings: {
    timezone: 'Europe/Vienna',
    workday: {
      start: '08:00',
      end: '22:00',
      hard_stop: '22:00',
      max_blocks_per_day: 8,
      max_hours_per_day: 10,
    },
    pomodoro: {
      work_minutes: 25,
      short_break_minutes: 5,
      long_break_minutes: 15,
      long_break_every: 4,
    },
  },
}

const FocusContext = createContext(null)

export function FocusProvider({ children }) {
  const [enabled, setEnabled] = useState(DEFAULTS.enabled)
  const [settings, setSettings] = useState(DEFAULTS.settings)

  // Active cycle as decided by Focus Gatekeeper
  const [activeCycle, setActiveCycle] = useState(null)
  // Derived timing state for header badge and consumers
  const [now, setNow] = useState(() => Date.now())

  // Maintain a ticking clock when a cycle is active
  const tickRef = useRef(null)

  // Load persisted settings
  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        // CRITICAL: Only load settings if user is authenticated
        // This prevents API calls on signin page where no user exists
        if (!BackendAuth.isAuthenticated()) {
          // console.log('[FocusProvider] Skipping settings load - no user authenticated')
          return
        }
        // Website-setup trial (/setup/<id>?trial=…): endpoint answers 403 TRIAL_FORBIDDEN.
        if (isTrialSession()) return

        const response = await BackendAuth.apiCallJson('/api/tickets/focus-gatekeeper/settings')
        if (!mounted || !response) return
        setEnabled(Boolean(response.enabled))
        if (response.user_settings) {
          setSettings((prev) => deepMerge(prev, response.user_settings))
        }
      } catch (err) {
        // console.warn('FocusProvider: settings load failed', err)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [])

  // Keep a 1s ticker active while a cycle is running
  useEffect(() => {
    if (activeCycle) {
      tickRef.current && clearInterval(tickRef.current)
      tickRef.current = setInterval(() => setNow(Date.now()), 1000)
      return () => {
        tickRef.current && clearInterval(tickRef.current)
      }
    }
    // Cleanup if not active
    tickRef.current && clearInterval(tickRef.current)
    return undefined
  }, [activeCycle])

  const derived = useMemo(() => {
    if (!activeCycle) return { running: false, timeLeftSec: 0, elapsedSec: 0, mode: null }
    const startsAt = new Date(activeCycle.starts_at).getTime()
    const endsAt = new Date(activeCycle.ends_at).getTime()
    const clampedNow = Math.max(startsAt, now)
    const elapsedSec = Math.max(0, Math.floor((clampedNow - startsAt) / 1000))
    const timeLeftSec = Math.max(0, Math.floor((endsAt - clampedNow) / 1000))
    return {
      running: timeLeftSec > 0,
      timeLeftSec,
      elapsedSec,
      mode: 'work',
    }
  }, [activeCycle, now])

  const startFromDecision = (decision) => {
    try {
      if (!decision) return
      if (decision.action !== 'start' || !decision.cycle) return
      setActiveCycle({ ...decision.cycle })
    } catch (err) {
      // console.warn('FocusProvider: startFromDecision failed', err)
    }
  }

  const stopCycle = async () => {
    try {
      await BackendAuth.apiCallJson('/api/tickets/focus-gatekeeper/decision', {
        method: 'POST',
        body: JSON.stringify({ intent: 'stop_cycle', user_settings: settings }),
      })
    } catch (err) {
      // Non-fatal for UI; still clear local state
      // console.warn('FocusProvider: stopCycle request failed', err)
    } finally {
      setActiveCycle(null)
    }
  }

  const value = useMemo(
    () => ({
      enabled,
      settings,
      setSettings,
      setEnabled,
      activeCycle,
      setActiveCycle,
      ...derived,
      startFromDecision,
      stopCycle,
    }),
    [enabled, settings, activeCycle, derived]
  )

  return <FocusContext.Provider value={value}>{children}</FocusContext.Provider>
}

export function useFocus() {
  const ctx = useContext(FocusContext)
  if (!ctx) throw new Error('useFocus must be used within FocusProvider')
  return ctx
}

function deepMerge(base, overlay) {
  if (!overlay || typeof overlay !== 'object') return base
  const out = Array.isArray(base) ? [...base] : { ...base }
  for (const key of Object.keys(overlay)) {
    const src = base?.[key]
    const val = overlay[key]
    if (
      src && typeof src === 'object' && !Array.isArray(src) &&
      val && typeof val === 'object' && !Array.isArray(val)
    ) {
      out[key] = deepMerge(src, val)
    } else {
      out[key] = val
    }
  }
  return out
}

